/**
 * Our custom versions of the individual components rendered in the Treebeard tree, which are
 * referred to as 'decorators' in Treebeard
 *
 * Note that we specify our own classes in these components rather than using Treebeard's `style`
 * object
 *
 * The 'Header' is defined in a separate file because it contains quite a bit of code
 */

import React, { ReactNode, createRef, Component, RefObject } from "react";
import classNames from "classnames";
import { TreeNodeProps } from "react-treebeard";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { FolderTreeId, FolderAnimationStates } from "./FolderTreeViewStore";
import { TreebeardHeader } from "./TreebeardHeader";
import { TransitionGroup, Transition } from "react-transition-group";
import { TransitionStatus } from "react-transition-group/Transition";
import forceReflow from "../../../utils/forceReflow";
import { ChevronRight } from "../../icons";

type FolderDragFn = (event: React.DragEvent, folderId: FolderTreeId) => void;
type NameChangeFn = (folderId: FolderTreeId, newName: string) => void;
type RenameConfirmFn = (folderId: FolderTreeId, newName: string) => void;
type FolderChangeFn = (folderId: FolderTreeId) => void;

const FOLDER_HEIGHT = 48;

/**
 * Props that are shared between all folders of the tree. This is defined separately from
 * `ITreebeardFolder` for readability
 */
interface ISharedTreeProps {
  onDragStart: FolderDragFn;
  onDragEnd: () => void;

  onDropOnFolder?: FolderDragFn;

  // These rename callback functions are passed through from the <FolderTree> props
  onNameChange?: NameChangeFn;
  onRenameConfirm?: RenameConfirmFn;
  onRenameCancel?: FolderChangeFn;

  maxNameLength: number;
  treeParentRef?: RefObject<HTMLElement>;
  onCreateFolder?: FolderChangeFn;
  onDeleteFolder?: FolderChangeFn;
  disableOptions?: boolean;
}

/**
 * The data structure that represents a single folder in the `data` object we pass into <Treebeard>
 *
 * @NOTE (som, 2020-04-01): it's initially tempting to have something like a
 * `sharedProps: ISharedTreeProps` object instead of extending `ISharedTreeProps`. While this would
 * greatly reduce the number of props, it also means that any changes inside `sharedProps` will not
 * be picked up by React (unless we make `sharedProps` a MobX store, which seems a bit overkill).
 */
interface ITreebeardFolder extends ISharedTreeProps {
  id: FolderTreeId;
  name: string;
  toggled: boolean;
  selected: boolean;
  renaming: boolean;
  isBeingDragged: boolean;
  children: ITreebeardFolder[];

  draggable: boolean;

  // Animation state and callback
  animationState?: FolderAnimationStates;
  isLoading?: boolean;
  disableSelect: boolean;
}

interface IContainerProps extends TreeNodeProps {
  node: ITreebeardFolder;
  onClick: () => void;
}

/**
 * The 'container' contains all the components in a single line of the folder tree.
 * Copied and adapted from the default Container decorator in react-treebeard.
 */

@observer
class Container extends Component<IContainerProps> {
  private containerRef: RefObject<HTMLDivElement>;

  @observable.ref
  private containerTop = 0;

  @observable.ref
  private treeParentTop = 0;

  constructor(props: IContainerProps) {
    super(props);
    this.containerRef = createRef<HTMLDivElement>();
  }

  public componentDidMount(): void {
    const { treeParentRef } = this.props.node;
    this.containerTop = this.containerRef.current?.getBoundingClientRect().top ?? 0;
    this.treeParentTop = treeParentRef?.current?.getBoundingClientRect().top ?? 0;
  }

  public render(): ReactNode {
    const { isLoading, animationState, children } = this.props.node;
    const isTerminal = children.length === 0;

    const renderContent = (status?: string): ReactNode => {
      // Apply the transform when the loading item hasn't entered so it can transition to
      // its non-transformed position on enter
      const showLoadng = isLoading && status !== "entered";
      const transform = showLoadng
        ? `translate3d(0, -${this.containerTop - this.treeParentTop - FOLDER_HEIGHT}px, 0)`
        : undefined;

      return (
        <div
          className={classNames({
            "treebeard-container__content": true,
            [`treebeard-container__content--${status}`]: true,
            "treebeard-container__content--loading": isLoading,
          })}
          style={{ transform }}
        >
          {isTerminal ? (
            // If this folder doesn't have any sub-folders, we render an empty spacer instead of the toggle icon
            <div className="treebeard-toggle-space" />
          ) : (
            <Toggle {...this.props} />
          )}
          <TreebeardHeader {...this.props} />
        </div>
      );
    };

    // @NOTE (som, 2020-01-13): instead of using the `decorator` prop provided by Treebeard to
    // reference `decorator.Toggle` and `decorator.Header`, we'll just reference these components
    // directly. This just makes things easier when we don't need the extra flexibility of swapping
    // out different Headers and Toggles
    return (
      <div className="treebeard-container" ref={this.containerRef}>
        <TransitionGroup component={null}>
          {!!animationState && animationState === "visible" && this.containerTop !== undefined && (
            <Transition appear timeout={400} onEnter={forceReflow}>
              {(status: TransitionStatus): ReactNode => {
                return renderContent(status);
              }}
            </Transition>
          )}
        </TransitionGroup>
        {!animationState && renderContent("entered")}
      </div>
    );
  }
}

/**
 * The component for opening/closing a folder
 */
function Toggle(props: IContainerProps): React.ReactElement {
  const {
    onClick,
    node: { toggled, id },
  } = props;

  return (
    <button
      className={classNames("treebeard-toggle", { "treebeard-toggle--open": toggled })}
      onClick={onClick}
      aria-expanded={toggled}
      aria-labelledby={`tree-${id}-folder`}
      id={`tree-${id}-toggle`}
    >
      <ChevronRight />
    </button>
  );
}

// @TODO (som, 2019-12-16): add a loading state once there is a design for it
// Loading: (props: any): React.ReactElement => {
//   return (
//     <div>
//       loading...
//     </div>
//   );
// },

const customDecorators = {
  Toggle,
  Header: TreebeardHeader,
  Container,
};

const customTheme = {
  tree: {
    base: {
      background: "transparent",
    },
  },
};

export {
  customDecorators,
  customTheme,
  ITreebeardFolder,
  ISharedTreeProps,
  IContainerProps,
  FolderDragFn,
  NameChangeFn,
  RenameConfirmFn,
  FolderChangeFn,
};
